import { type ReactElement, type ReactNode } from 'react';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { ErrorCallout } from '@/components/ui/error-callout';

/**
 * `ErrorCallout` + `ApiError` code line + optional retry, for any list/page
 * fetch. Pass localized strings; feature routes stay the source of i18n keys.
 *
 * - If `actions` is set, it is rendered as-is (e.g. retry + back link).
 * - Else if `onRetry` is set, a single default retry button is shown (`retryLabel` required).
 */
export function FetchErrorCallout({
  error,
  heading,
  message,
  codeLabel,
  onRetry,
  retryLabel,
  actions,
  headingAs = 'h2',
  className,
  calloutClassName,
  role = 'alert',
}: {
  readonly error: unknown;
  readonly heading: string;
  readonly message: string;
  readonly codeLabel: string;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
  readonly actions?: ReactNode;
  readonly headingAs?: 'p' | 'h2' | 'h3' | 'h4';
  /** Wrapper (e.g. `max-w-xl` + padding). */
  readonly className?: string;
  readonly calloutClassName?: string;
  readonly role?: 'alert' | 'status';
}): ReactElement {
  const code = getErrorCodeForUi(error);

  const resolvedActions: ReactNode =
    actions !== undefined ? (
      actions
    ) : onRetry !== undefined && retryLabel !== undefined ? (
      <Button onClick={onRetry}>{retryLabel}</Button>
    ) : undefined;

  return (
    <div className={className}>
      <ErrorCallout
        role={role}
        headingAs={headingAs}
        {...(calloutClassName !== undefined ? { className: calloutClassName } : {})}
        heading={heading}
        message={message}
        code={code}
        codeLabel={codeLabel}
        actions={resolvedActions}
      />
    </div>
  );
}
